import { Form } from "antd/es";
import type { FormInstance } from "antd/es/form";
import React from "react";
import { KMainFunctions } from "../shared/utilty/main-functions";

declare let window: any;

interface IMasterFormContextValue {
  form: FormInstance | null;
}

export const MasterFormContext = React.createContext<IMasterFormContextValue>({ form: null });

interface IMasterFormBridgeProps {
  children: React.ReactNode;
}

const MasterFormBridge: React.FC<IMasterFormBridgeProps> = ({ children }) => {
  const [form] = Form.useForm();
  return <MasterFormContext.Provider value={{ form }}>{children}</MasterFormContext.Provider>;
};

interface IMasterScreenWrapperProps {
  children?: React.ReactNode;
}

interface IMasterScreenWrapperState {
  privateRouteReady: boolean;
}

export abstract class MasterScreenWrapper extends React.PureComponent<
  IMasterScreenWrapperProps,
  IMasterScreenWrapperState
> {
  private _handler: (() => void) | null = null;

  constructor(props: IMasterScreenWrapperProps) {
    super(props);
    this.state = {
      privateRouteReady: window.userInfo !== undefined
    };
  }

  protected abstract get anonymousPaths(): string[];
  protected abstract get MasterComponent(): React.ComponentType<any>;
  protected abstract get MasterProvider(): React.ComponentType<any>;
  protected abstract renderSwitchWrapper(): React.ReactNode;

  // When true, render master immediately and skip the privateRouteReady auth gate.
  // The router-level wrapper leaves this false (default) so master chrome doesn't flash
  // before login resolves. Inline-mount subclasses (drawer/modal/floatingPanel) override
  // to true: by definition they only mount inside an already-routed (already-authed)
  // parent screen, so the auth gate is meaningless and can incorrectly suppress master
  // when window.userInfo isn't readable at construction time for any reason.
  protected get forceReady(): boolean {
    return false;
  }

  componentDidMount() {
    if (this.state.privateRouteReady) return;
    this._handler = () => this.setState({ privateRouteReady: true });
    window.addEventListener("privateRouteContextReady", this._handler);
  }

  componentWillUnmount() {
    if (this._handler) {
      window.removeEventListener("privateRouteContextReady", this._handler);
      this._handler = null;
    }
  }

  private isPathAnonymous(): boolean {
    const currentPath = window.location.hash
      ? window.location.hash.replace(/^#/, "").split("?")[0]
      : (() => {
          const virtualDir = KMainFunctions.virtualDirectoryURL === "auto" ? "" : KMainFunctions.virtualDirectoryURL;
          return window.location.pathname.replace(virtualDir, "").split("?")[0];
        })();
    return this.anonymousPaths.some((p) => currentPath?.toLowerCase() === p);
  }

  render() {
    const { privateRouteReady } = this.state;
    const switchContent = this.renderSwitchWrapper();

    if (!this.forceReady && !privateRouteReady && !this.isPathAnonymous()) {
      return switchContent;
    }

    const Master = this.MasterComponent;
    const Provider = this.MasterProvider;

    return (
      <MasterFormBridge>
        <Provider>
          <Master>{switchContent}</Master>
        </Provider>
      </MasterFormBridge>
    );
  }
}
